import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import googleIcon from '../../media/google.svg'
import outlookIcon from '../../media/outlook.svg'
import TwoFactorVerification from './TwoFactorVerification'
import useGlobalTheme from '../../hooks/useGlobalTheme'

import NavBar from '../LandingPage/NavBar'

function Register() {
	const navigate = useNavigate()
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [errorMessage, setErrorMessage] = useState('')
	const [consentChecked, setConsentChecked] = useState(true)
	const [isAuthenticating, setIsAuthenticating] = useState(false)
	const [showPassword, setShowPassword] = useState(false)
	const [showConfirmPassword, setShowConfirmPassword] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [twoFactorState, setTwoFactorState] = useState(null)

	const location = useLocation()
	const isFromExtension =
		new URLSearchParams(location.search).get('from') === 'extension'

	const devURI = 'http://localhost:5004'
	const productionURI = ''
	const isLocalhost = window.location.hostname === 'localhost'
	const authUrl = isLocalhost ? devURI : productionURI
	let googleAuthUrl = `${authUrl}/auth/google`
	let outlookAuthUrl = `${authUrl}/auth/outlook`

	const handleLoginSuccess = useCallback(data => {
		localStorage.setItem('jwtToken', data.token)
		if (data.email) localStorage.setItem('email', data.email)

		const searchParams = new URLSearchParams(location.search)
		const isFromExtension = searchParams.get('from') === 'extension'

		if (isFromExtension && window.opener) {
			window.opener.postMessage(
				{ type: 'AUTH_SUCCESS', payload: { token: data.token } },
				'*'
			)
			window.close()
		} else {
			navigate('/dashboard')
		}
	}, [navigate, location.search])

	const handleSubmit = async e => {
		e.preventDefault()

		if (!email.trim()) {
			setErrorMessage('Please enter an email address.')
			return
		}

		if (!password) {
			setErrorMessage('Please enter a password.')
			return
		}

		if (!confirmPassword) {
			setErrorMessage('Please confirm your password.')
			return
		}

		if (password !== confirmPassword) {
			setErrorMessage('Passwords do not match.')
			return
		}

		if (!consentChecked) {
			setErrorMessage(
				'You must consent to our Privacy Policy and Terms and Condition to continue.'
			)
			return
		}

		setIsSubmitting(true)
		try {
			const response = await fetch('/api/auth/register', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({ email, password }),
			})

			const data = await response.json()

			if (response.ok) {
				if (data.requiresTwoFactor) {
					setTwoFactorState({
						pendingLoginToken: data.pendingLoginToken,
						maskedEmail: data.maskedEmail,
					})
				} else if (data.token) {
					handleLoginSuccess(data)
				}
			} else {
				setErrorMessage(data.message)
			}
		} catch (error) {
			console.error('Error during registration:', error)
			setErrorMessage('An error occurred during registration.')
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleGoogleLogin = () => {
		if (!consentChecked) {
			setErrorMessage(
				'You must consent to our Privacy Policy and Terms and Condition to continue.'
			)
			return
		}

		const w = 500
		const h = 600
		const y = window.top.outerHeight / 2 + window.top.screenY - h / 2
		const x = window.top.outerWidth / 2 + window.top.screenX - w / 2

		window.open(
			googleAuthUrl,
			'Google Sign-In',
			`toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes, copyhistory=no, width=${w}, height=${h}, top=${y}, left=${x}`
		)
	}

	const handleOutlookLogin = () => {
		if (!consentChecked) {
			setErrorMessage(
				'You must consent to our Privacy Policy and Terms and Condition to continue.'
			)
			return
		}

		if (isAuthenticating) return
		setIsAuthenticating(true)

		const w = 500
		const h = 600
		const y = window.top.outerHeight / 2 + window.top.screenY - h / 2
		const x = window.top.outerWidth / 2 + window.top.screenX - w / 2

		const outlookWindow = window.open(
			outlookAuthUrl,
			'Outlook Sign-In',
			`toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes, copyhistory=no, width=${w}, height=${h}, top=${y}, left=${x}`
		)

		const checkWindow = setInterval(() => {
			if (outlookWindow.closed) {
				clearInterval(checkWindow)
				setIsAuthenticating(false)
			}
		}, 1000)
	}

	useEffect(() => {
		if (errorMessage) {
			const timer = setTimeout(() => setErrorMessage(''), 5000)
			return () => clearTimeout(timer)
		}
	}, [errorMessage])

	useEffect(() => {
		const handlePostMessage = event => {
			const { origin } = event
			const isValidOrigin =
				origin === window.location.origin ||
				origin === devURI ||
				origin === productionURI

			if (!isValidOrigin) return

			const { error, message, token, email, requiresTwoFactor, pendingLoginToken, maskedEmail } = event.data

			if (requiresTwoFactor && pendingLoginToken) {
				setTwoFactorState({ pendingLoginToken, maskedEmail })
			} else if (error) {
				setErrorMessage(message)
			} else if (token && email) {
				handleLoginSuccess({ token, email })
			}
		}

		window.addEventListener('message', handlePostMessage)
		return () => window.removeEventListener('message', handlePostMessage)
	}, [navigate, location.search, handleLoginSuccess])

	useEffect(() => {
		const searchParams = new URLSearchParams(location.search)
		const error = searchParams.get('error')
		const message = searchParams.get('message')

		if (error && message) {
			setErrorMessage(message)
		}
	}, [location.search])

	if (twoFactorState) {
		return (
			<>
				{!isFromExtension && <NavBar isNotLandingPage={true} />}
				<div className={`flex justify-center items-start pt-10 pb-10 ${isLight ? 'bg-white' : 'bg-[#15202b]'} ${isFromExtension ? 'min-h-screen' : 'min-h-[calc(100vh-64px)]'}`}>
					<TwoFactorVerification
						pendingLoginToken={twoFactorState.pendingLoginToken}
						maskedEmail={twoFactorState.maskedEmail}
						onCancel={() => setTwoFactorState(null)}
						onSuccess={handleLoginSuccess}
					/>
				</div>
			</>
		)
	}

	return (
		<>
			{!isFromExtension && <NavBar isNotLandingPage={true} />}
			<div className={`flex justify-center items-start pt-10 pb-10 ${isLight ? 'bg-white' : 'bg-[#15202b]'} ${isFromExtension ? 'min-h-screen' : 'min-h-[calc(100vh-64px)]'}`}>
				<div className={`p-8 rounded-lg w-full max-w-md ${isLight ? 'bg-white ring-1 ring-gray-200 shadow-lg text-gray-900' : 'bg-gray-800 ring-1 ring-white/10 shadow-xl text-white'}`}>
					<h2 className="text-2xl font-semibold mb-6 text-center">
						Create an account
					</h2>

					{errorMessage && (
						<div className={`text-center mb-4 ${isLight ? 'text-red-600' : 'text-red-400'}`}>{errorMessage}</div>
					)}

					<button
						className={`w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium bg-white text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center justify-center ${isLight ? 'focus:ring-offset-white' : 'focus:ring-offset-gray-800'}`}
						type="button"
						onClick={handleGoogleLogin}
					>
						<img
							src={googleIcon}
							alt="Google"
							style={{ width: '20px', height: '20px' }}
						/>
						<p className="btn-text ml-2">Continue with Google</p>
					</button>

					<button
						className={`w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium bg-white text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center justify-center mt-3 ${isLight ? 'focus:ring-offset-white' : 'focus:ring-offset-gray-800'}`}
						type="button"
						onClick={handleOutlookLogin}
						disabled={isAuthenticating}
					>
						<img
							src={outlookIcon}
							alt="Outlook"
							style={{ width: '20px', height: '20px' }}
						/>
						<p className="btn-text ml-2">
							{isAuthenticating ? 'Connecting...' : 'Continue with Outlook'}
						</p>
					</button>

					<div className="relative mt-2 mb-0">
						<div className="absolute inset-0 flex items-center">
							<div className={`w-full border-t ${isLight ? 'border-gray-200' : 'border-gray-700'}`}></div>
						</div>
						<div className="relative flex justify-center text-sm">
							<span className={`px-4 ${isLight ? 'bg-white text-gray-500' : 'bg-gray-800 text-gray-400'}`}>OR</span>
						</div>
					</div>

					<form onSubmit={handleSubmit}>
						<div className="space-y-0">
							<div>
								<label
									htmlFor="email"
									className={`block text-sm font-medium ${isLight ? 'text-gray-700' : 'text-gray-300'}`}
								>
									Email
								</label>
								<input
									type="email"
									id="email"
									value={email}
									onChange={e => setEmail(e.target.value)}
									className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${isLight ? 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400' : 'bg-gray-700 border border-gray-600 text-white'}`}
								/>
							</div>
							<div>
								<label
									htmlFor="password"
									className={`block text-sm font-medium ${isLight ? 'text-gray-700' : 'text-gray-300'}`}
								>
									Password
								</label>
								<div className="relative mt-1">
									<input
										type={showPassword ? 'text' : 'password'}
										id="password"
										value={password}
										onChange={e => setPassword(e.target.value)}
										className={`block w-full rounded-md shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${isLight ? 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400' : 'bg-gray-700 border border-gray-600 text-white'}`}
									/>
									<button
										type="button"
										onClick={() => setShowPassword(prev => !prev)}
										className={`absolute inset-y-0 right-0 flex items-center pr-3 ${isLight ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-200'}`}
									>
										{showPassword ? (
											<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
												<path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
											</svg>
										) : (
											<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
												<path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
												<path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
											</svg>
										)}
									</button>
								</div>
							</div>
							<div>
								<label
									htmlFor="confirmPassword"
									className={`block text-sm font-medium ${isLight ? 'text-gray-700' : 'text-gray-300'}`}
								>
									Confirm password
								</label>
								<div className="relative mt-1">
									<input
										type={showConfirmPassword ? 'text' : 'password'}
										id="confirmPassword"
										value={confirmPassword}
										onChange={e => setConfirmPassword(e.target.value)}
										className={`block w-full rounded-md shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${isLight ? 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400' : 'bg-gray-700 border border-gray-600 text-white'}`}
									/>
									<button
										type="button"
										onClick={() => setShowConfirmPassword(prev => !prev)}
										className={`absolute inset-y-0 right-0 flex items-center pr-3 ${isLight ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-200'}`}
									>
										{showConfirmPassword ? (
											<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
												<path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
											</svg>
										) : (
											<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
												<path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
												<path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
											</svg>
										)}
									</button>
								</div>
							</div>
						</div>

						<div className="mt-4">
							<div className="flex items-center mb-2">
								<input
									id="consent"
									type="checkbox"
									checked={consentChecked}
									onChange={e => setConsentChecked(e.target.checked)}
									className={`h-4 w-4 text-[#1d9bf0] focus:ring-[#1d9bf0] rounded ${isLight ? 'bg-white border-gray-300' : 'bg-gray-700 border-gray-600'}`}
								/>
								<label
									htmlFor="consent"
									className={`ml-2 block text-sm ${isLight ? 'text-gray-700' : 'text-gray-300'}`}
								>
									By signing up, you agree to our{' '}
									<a href="/policy" className="text-sky-400 hover:text-sky-300 hover:underline">
										Privacy Policy
									</a>{' '}
									and{' '}
									<a href="/terms" className="text-sky-400 hover:text-sky-300 hover:underline">
										Terms and Conditions
									</a>
								</label>
							</div>
						</div>
						<div>
							<button
								type="submit"
								disabled={isSubmitting}
								className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#1d9bf0] hover:bg-[#1a8cd8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${isLight ? 'focus:ring-offset-white' : 'focus:ring-offset-gray-800'}`}
							>
								{isSubmitting && (
									<svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
										<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
										<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
									</svg>
								)}
								{isSubmitting ? 'Creating account...' : 'Create an account'}
							</button>
						</div>
					</form>
					<div className={`mt-3 text-sm text-center ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
						Already have an account?{' '}
						<a
							href={`/login${location.search}`}
							className="text-sky-400 hover:text-sky-300 hover:underline"
						>
							Login here
						</a>
					</div>
				</div>
			</div>
		</>
	)
}

export default Register
